import {
  mapTitle,
  mapText,
  mapImage,
  normalizeSpacer,
  asArray,
} from "@/components/DesignSystem/utils/teaserHelper"
import { omit } from "lodash"
import { handleModalInteractions } from "@/components/DesignSystem/analytics/featureCardSliderAnalytics"
import { validateFeatureCardsWithModalSliderProps } from "../validators/featuredCardsWithModalSliderValidator"

const mapModalContentSections = (sections = []) => {
  const mapped = sections
    .map(sec => {
      const heading = mapTitle(sec?.image?.heading)
      const copy = mapText(sec?.copy)
      const image = mapImage(omit(sec?.image, ["heading", "href"]))
      const isFullWidth =
        typeof sec?.isFullWidth === "boolean" ? sec.isFullWidth : undefined

      if (!heading && !copy && !image && isFullWidth === undefined)
        return undefined

      return {
        ...(heading ? { heading } : {}),
        ...(copy ? { copy } : {}),
        ...(image ? { image } : {}),
        ...(isFullWidth !== undefined ? { isFullWidth } : {}),
      }
    })
    .filter(Boolean)

  return mapped.length ? mapped : undefined
}

const mapModal = (modal, mainHeading = "") => {
  if (!modal) return undefined
  const heading = mapTitle(modal?.heading)
  const content = mapModalContentSections(modal?.content || [])
  if (!heading && !content) return undefined
  return {
    ...(heading ? { heading } : {}),
    ...(content ? { content } : {}),
    onModalClose: () => handleModalInteractions(heading?.text, mainHeading),
  }
}

const mapCard = (card, mainTitle) => {
  const { text: mainHeading = "" } = mainTitle || {}
  const heading = mapTitle(card?.heading?.[0])
  const image = mapImage(card?.image?.[0])
  const modalContent = mapModal(card?.modalContent?.[0], mainHeading)
  const imageActionCTA = {
    onClick: () => handleModalInteractions(heading?.text, mainHeading, true),
  }

  if (!heading && !image && !imageActionCTA) return undefined

  return {
    ...(heading ? { heading } : {}),
    ...(image ? { image } : {}),
    ...(modalContent ? { modalContent } : {}),
    ...(imageActionCTA ? { imageActionCTA } : {}),
  }
}

/**
 * Maps Feature Cards With Modal Slider content container and its teaser children to props for the component.
 *
 * @param {Object} container - The container object, possibly with style/class info.
 * @return {Object} Mapped props including heading, description, cards, and background.
 */
export function mapFeatureCardsSliderProps(container) {
  const firstKey =
    container?.[":itemsOrder"]?.[0] ??
    Object.keys(container?.[":items"] ?? {})[0]

  const node = firstKey ? container?.[":items"]?.[firstKey] : undefined

  if (!node) return null
  const basic = node.basic
  const media = node.media
  const mod = basic?.mod || "white"
  const heading = mapTitle(basic?.heading)
  const cards = asArray(media?.media)
    ?.map(item => mapCard(item, heading))
    .filter(Boolean)
    .map(item => ({
      ...item,
    }))

  const topSpacer = basic.topSpacer
    ? normalizeSpacer(basic.topSpacer.size)
    : undefined
  const bottomSpacer = basic.bottomSpacer
    ? normalizeSpacer(basic.bottomSpacer.size)
    : undefined

  const featuredCardsWithModalSliderProps = {
    ...(mod ? { mod } : {}),
    ...(heading ? { heading } : {}),
    ...(topSpacer ? { topSpacer } : {}),
    ...(bottomSpacer ? { bottomSpacer } : {}),
    ...(cards?.length ? { cards } : {}),
  }

  const { isValid, errors } = validateFeatureCardsWithModalSliderProps(
    featuredCardsWithModalSliderProps
  )

  if (!isValid) {
    console.warn("featuredCardsWithModalSliderProps validation errors:", errors)
    return null
  }

  return featuredCardsWithModalSliderProps
}
